import { useContext, useEffect, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useNavigationUrl } from "../../hooks/useNavigationUrl";
import NewLoader from "../../component/reusableComponent/newLoader/NewLoader";
import { ROUTES } from "../../constant";
import { StateContext } from "../../context/globalContext/context";
import { iGlobalContext } from "../../context/globalContext/interface";
import { ScreenTypes } from "../../enums";
import styles from "./stripeSuccess.module.scss";

import Confetti from "../../component/reusableComponent/confetti/Confetti";
import BookingNavBar from "../../component/reusableComponent/bookingNavBar/bookingNavBar";
import BookingConfirmation from "../../component/reusableComponent/checkout/BookingConfirmation";
import useBookingConfirm from "../../component/reusableComponent/checkout/useBookingConfirm";
import useWebbedHotelNotes from "../../component/Payments/paymentSuccessPage/hooks/useWebbedHotelNotes";
import WebbedHotelNotes from "../../component/reusableComponent/webbedHotelNotes/WebbedHotelNotes";
import { clearChatReturnAnchor } from "../../services/searchProgress/chatReturnAnchor";
import { clearChatBookingOrigin } from "../../utils/booking/clearChatBookingOrigin";
import {
  getPortalSurface,
  isAppEmbedFlow,
  isPortalEmbedFlow,
  postToPortal,
  PORTAL_MESSAGES,
} from "../../utils/embedPortalBridge";
import { isEmbedMode } from "../../utils/embedMode";

export default function StripeSuccess() {
  const navigate = useNavigate();
  const { navigateWithParams } = useNavigationUrl();
  const [searchParams] = useSearchParams();
  const session_Id = searchParams.get("session_id") ?? "";
  const noPayment = searchParams.get("noPayment") === "1";
  const state = useContext(StateContext);
  const { setFirstScreen, setScreen, userInfo }: iGlobalContext = state;
  const {
    isLoading,
    setIsLoading,
    isBookingFailed,
    setIsBookingFailed,
    bookingInProgressFlag,
    itineraryID,
    isMobile,
    readAndClearChatOrigin,
    handleGotoTrip,
    handleViewItinerary,
  } = useBookingConfirm(session_Id, "booking", undefined, noPayment);
  // WebBeds requires its tariff notes + non-included property fees on the
  // booking confirmation; empty (renders nothing) for every other supplier.
  const webbedHotelNotes = useWebbedHotelNotes(itineraryID, !isLoading && !isBookingFailed);

  const portalEmbedFlow = isPortalEmbedFlow();

  const bookingAnnouncedRef = useRef(false);
  useEffect(() => {
    if (!portalEmbedFlow || isLoading || isBookingFailed) return;
    if (bookingAnnouncedRef.current) return;
    bookingAnnouncedRef.current = true;
    postToPortal({
      type: PORTAL_MESSAGES.bookingComplete,
      bookingRef: itineraryID || "",
    });
  }, [portalEmbedFlow, isLoading, isBookingFailed, itineraryID]);

  const clearBookingSessionMarkers = () => {
    clearChatBookingOrigin();
    clearChatReturnAnchor();
  };

  const returnToPortalSurface = () => {
    clearBookingSessionMarkers();
    const surface = getPortalSurface();
    if (surface === "history") {
      postToPortal({ type: PORTAL_MESSAGES.navigateSurface, surface: "chat" });
    }
    navigate(surface === "shop" ? ROUTES.PortalShop : ROUTES.PortalChat, { replace: true });
  };

  const goToTripInPortal = () => {
    clearBookingSessionMarkers();
    const dispatched = postToPortal({
      type: PORTAL_MESSAGES.goToBooking,
      bookingRef: itineraryID,
    });
    if (!dispatched) handleViewItinerary();
  };

  const appEmbedFlow = isAppEmbedFlow();

  const goToMyTripsInApp = () => {
    clearBookingSessionMarkers();
    navigate(ROUTES.PortalTrips, { replace: true });
  };

  return (
    <>
      <Confetti active={!isLoading && !!itineraryID && !isBookingFailed} count={240} />
      {/* <Navbar /> */}
      {!isEmbedMode() && (
        <BookingNavBar
          onLogoClick={() => {
            if (portalEmbedFlow || appEmbedFlow) {
              returnToPortalSurface();
              return;
            }
            // V2 chat (flight or hotel): same chat-vs-NewTrip routing as
            // handleGotoTrip above. Logo click on the success screen should
            // return the chat-origin user to the chat home, not the legacy
            // NewTrip form.
            if (readAndClearChatOrigin()) {
              if (isMobile) {
                navigateWithParams(ROUTES.TopDestination);
              } else {
                navigate(ROUTES.Home);
                setScreen("");
                setFirstScreen(ScreenTypes.topDestination);
              }
              return;
            }
            navigate(ROUTES.Home);
            setScreen("");
            setFirstScreen(ScreenTypes.NewTrip);
          }}
        />
      )}

      <div className={styles.successContainer}>
        {isLoading && (
          <NewLoader
            isOpen={isLoading}
            onClose={() => {
              setIsLoading(false);
              setIsBookingFailed(true);
            }}
            text="Please wait"
            loadFullScreen
            hideCancelBtn={true}
          />
        )}

        {!isLoading && !!itineraryID && !isBookingFailed && (
          <BookingConfirmation
            className={styles.mainContainer}
            inProgress={bookingInProgressFlag}
            itineraryId={itineraryID}
            email={userInfo?.email}
            notes={
              webbedHotelNotes.length > 0 ? (
                <div className={styles.webbedNotes}>
                  {webbedHotelNotes.map((notes, index) => (
                    <WebbedHotelNotes
                      key={`${notes.hotelName}-${index}`}
                      notes={notes}
                      showHotelName={webbedHotelNotes.length > 1}
                    />
                  ))}
                </div>
              ) : null
            }
            onGoToTrip={() =>
              portalEmbedFlow ? goToTripInPortal() : appEmbedFlow ? goToMyTripsInApp() : handleViewItinerary()
            }
            onPlanNewTrip={() => (portalEmbedFlow || appEmbedFlow ? returnToPortalSurface() : handleGotoTrip())}
          />
        )}
      </div>
    </>
  );
}
